import { styled } from '@mui/material/styles';
import React from 'react';
import { StaticSearchErrorPanel } from './static-search-error-panel';
import { StaticSearchLoadingPanel } from './static-search-loading-panel';

interface StaticSearchAutocompleteListboxProps extends React.HTMLAttributes<HTMLUListElement> {
    hasMore: boolean;
    isLastItemFocused: boolean;
    loadMoreError: boolean;
    loadMoreErrorVariant?: 'connection' | 'generic';
    isLoadingMore: boolean;
    onLoadMore: () => void;
}

const StyledListbox = styled('ul')({
    '& .Mui-focusVisible': {
        borderRadius: 2,
        outline: '2px solid black',
        outlineOffset: '-2px',
    },
    '& li': {
        cursor: 'pointer',
    },
    boxSizing: 'border-box',
    flex: 1,
    listStyle: 'none',
    margin: 0,
    overflowX: 'hidden',
    padding: '8px 0',
});

const StaticSearchAutocompleteListbox = React.forwardRef<HTMLUListElement, StaticSearchAutocompleteListboxProps>(
    function StaticSearchAutocompleteListbox(
        {
            children,
            hasMore,
            isLastItemFocused,
            isLoadingMore,
            loadMoreError,
            loadMoreErrorVariant = 'generic',
            onLoadMore,
            style,
            ...props
        },
        ref,
    ) {
        const listboxScrollRef = React.useRef<HTMLUListElement | null>(null);
        const sentinelRef = React.useRef<HTMLLIElement | null>(null);

        const listboxRefCallback = React.useCallback(
            (node: HTMLUListElement | null) => {
                listboxScrollRef.current = node;
                if (typeof ref === 'function') {
                    ref(node);
                } else if (ref && typeof ref === 'object') {
                    // eslint-disable-next-line no-param-reassign -- merging forwarded ref
                    (ref as { current: HTMLUListElement | null }).current = node;
                }
            },
            [ref],
        );

        const sentinelRefCallback = React.useCallback((node: HTMLLIElement | null) => {
            sentinelRef.current = node;
        }, []);

        React.useLayoutEffect(() => {
            if (isLastItemFocused && (isLoadingMore || loadMoreError)) {
                const listbox = listboxScrollRef.current;
                if (listbox) {
                    listbox.scrollTop = listbox.scrollHeight;
                }
            }
        }, [isLastItemFocused, isLoadingMore, loadMoreError]);

        React.useEffect(() => {
            const sentinel = sentinelRef.current;
            if (!sentinel || !hasMore) return undefined;
            const observer = new IntersectionObserver(
                entries => {
                    if (entries[0]?.isIntersecting) {
                        onLoadMore();
                    }
                },
                { threshold: 0 },
            );
            observer.observe(sentinel);
            return () => observer.disconnect();
        }, [hasMore, onLoadMore]);

        return (
            <div style={{ ...style, display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
                <StyledListbox ref={listboxRefCallback} {...props}>
                    {children}
                    {hasMore && !loadMoreError && (
                        <li ref={sentinelRefCallback} aria-hidden style={{ listStyle: 'none', padding: 0 }}>
                            {' '}
                        </li>
                    )}
                </StyledListbox>
                {loadMoreError && <StaticSearchErrorPanel variant={loadMoreErrorVariant} />}
                {isLoadingMore && <StaticSearchLoadingPanel />}
            </div>
        );
    },
);

export default StaticSearchAutocompleteListbox;

StaticSearchAutocompleteListbox.defaultProps = {
    loadMoreErrorVariant: 'generic',
};
